import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  ActionBarsChart,
  ActivityChart,
  BreakdownRow,
  ProductVisitsChart,
  TrendChip,
  WEB_STATS_BRAND,
  WebsiteMetricCard,
  actorLabel,
  categoryLabel,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  safeNumber,
  sourceLabel,
} from "../WebsiteStatsUi";

const PRESETS = [
  { key: "today", label: "Hoy" },
  { key: "7days", label: "7 d" },
  { key: "30days", label: "30 d" },
  { key: "month", label: "Mes" },
  { key: "custom", label: "Fechas" },
];

const STATS_NAV = [
  { id: "stats-summary", label: "Resumen" },
  { id: "stats-products", label: "Productos" },
  { id: "stats-actions", label: "Acciones" },
  { id: "stats-details", label: "Detalle" },
];

function scrollToStatsSection(id) {
  if (typeof document === "undefined") return;
  const target = document.getElementById(id);
  if (!target) return;
  if (target.tagName === "DETAILS") target.open = true;
  target.scrollIntoView({ behavior: "smooth", block: "start" });
}

function MobileStatsNav() {
  return (
    <Paper elevation={0} sx={{ position: "sticky", top: 62, zIndex: 5, p: .75, borderRadius: 2.8, border: `1px solid ${WEB_STATS_BRAND.border}`, bgcolor: "rgba(255,255,255,.95)", backdropFilter: "blur(10px)" }}>
      <Box sx={{ display: "flex", gap: .45, overflowX: "auto" }}>
        {STATS_NAV.map((item) => (
          <Button key={item.id} size="small" onClick={() => scrollToStatsSection(item.id)} sx={{ minWidth: "auto", px: 1.15, borderRadius: 999, color: WEB_STATS_BRAND.dark, textTransform: "none", fontWeight: 900, flexShrink: 0 }}>
            {item.label}
          </Button>
        ))}
      </Box>
    </Paper>
  );
}

function MobileSection({ id, icon, title, helper, collapsible = false, children }) {
  return (
    <Paper
      id={id}
      component={collapsible ? "details" : "div"}
      elevation={0}
      sx={{
        p: 1.4,
        borderRadius: 3.2,
        border: `1px solid ${WEB_STATS_BRAND.border}`,
        scrollMarginTop: 120,
        "&[open] .stats-section-chevron": { transform: "rotate(180deg)" },
        "&[open] > .stats-section-summary": { mb: 1.2 },
      }}
    >
      <Stack
        className="stats-section-summary"
        component={collapsible ? "summary" : "div"}
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          mb: collapsible ? 0 : 1.2,
          minHeight: collapsible ? 44 : "auto",
          cursor: collapsible ? "pointer" : "default",
          listStyle: "none",
          "&::-webkit-details-marker": { display: "none" },
        }}
      >
        <Box sx={{ color: WEB_STATS_BRAND.orange, display: "flex", flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 950, fontSize: 16.5, lineHeight: 1.2 }}>{title}</Typography>
          {helper ? <Typography variant="caption" sx={{ display: "block", color: WEB_STATS_BRAND.muted, lineHeight: 1.25 }}>{helper}</Typography> : null}
        </Box>
        {collapsible ? <ExpandMoreRoundedIcon className="stats-section-chevron" sx={{ color: WEB_STATS_BRAND.muted, transition: "transform .18s ease", flexShrink: 0 }} /> : null}
      </Stack>
      {children}
    </Paper>
  );
}

function MiniInsight({ label, value, helper }) {
  return (
    <Box sx={{ p: 1.15, borderRadius: 2.5, bgcolor: WEB_STATS_BRAND.background }}>
      <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted, fontWeight: 900 }}>{label}</Typography>
      <Typography sx={{ color: WEB_STATS_BRAND.dark, fontWeight: 950, fontSize: 16 }}>{value}</Typography>
      {helper ? <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{helper}</Typography> : null}
    </Box>
  );
}

function catalogCategoryLabel(value) {
  const label = String(value || "").trim();
  return label || "Sin categoría";
}

function itemTypeLabel(value) {
  return value === "service" ? "Servicio" : "Producto";
}

function MobileTopProduct({ row, index }) {
  return (
    <Stack direction="row" spacing={.8} alignItems="center" sx={{ p: .9, borderRadius: 2.3, bgcolor: WEB_STATS_BRAND.background }}>
      <Box sx={{ width: 28, height: 28, borderRadius: 1.8, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: WEB_STATS_BRAND.orange, fontWeight: 950, flexShrink: 0 }}>{index + 1}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap variant="body2" sx={{ fontWeight: 950, color: WEB_STATS_BRAND.dark }}>{row.name || "Producto o servicio"}</Typography>
        <Typography noWrap variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{itemTypeLabel(row.itemType)} · {catalogCategoryLabel(row.category)}</Typography>
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Stack direction="row" spacing={.35} alignItems="center" justifyContent="flex-end">
          <VisibilityRoundedIcon sx={{ fontSize: 15, color: WEB_STATS_BRAND.orange }} />
          <Typography sx={{ fontWeight: 950, color: WEB_STATS_BRAND.dark, fontSize: 14 }}>{formatNumber(row.views)}</Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{formatNumber(row.uniqueVisitors)} personas</Typography>
      </Box>
    </Stack>
  );
}

function MobileEvent({ event }) {
  return (
    <Box component="details" sx={{ py: .85, borderBottom: `1px solid ${WEB_STATS_BRAND.border}`, "&:last-child": { borderBottom: 0 }, "&[open] .event-chevron": { transform: "rotate(180deg)" } }}>
      <Stack component="summary" direction="row" spacing={.8} alignItems="center" sx={{ minHeight: 42, cursor: "pointer", listStyle: "none", "&::-webkit-details-marker": { display: "none" } }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark, fontSize: 14 }}>{event.title || event.action}</Typography>
          <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap" sx={{ mt: .45 }}>
            <Chip size="small" label={categoryLabel(event.category)} />
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.48)", alignSelf: "center" }}>{formatDateTime(event.occurredAt)}</Typography>
          </Stack>
        </Box>
        <ExpandMoreRoundedIcon className="event-chevron" sx={{ color: WEB_STATS_BRAND.muted, transition: "transform .18s ease", flexShrink: 0 }} />
      </Stack>
      <Typography variant="caption" sx={{ display: "block", color: WEB_STATS_BRAND.muted, mt: .55, lineHeight: 1.45 }}>{event.description || "Movimiento registrado."}</Typography>
      <Stack direction="row" spacing={.55} useFlexGap flexWrap="wrap" sx={{ mt: .65 }}>
        <Chip size="small" variant="outlined" label={sourceLabel(event.source)} />
        <Chip size="small" variant="outlined" label={actorLabel(event)} />
      </Stack>
    </Box>
  );
}

export default function MobileWebsiteStats({ analytics, range, onPreset, onRangeChange, onRefresh, loading, error, rangeError }) {
  const totals = analytics?.totals || {};
  const comparisons = analytics?.comparisons || {};
  const insights = analytics?.insights || {};
  const categories = analytics?.categories || [];
  const sources = analytics?.sources || [];
  const actions = analytics?.actions || [];
  const team = analytics?.team || [];
  const paths = analytics?.paths || [];
  const recentEvents = analytics?.recentEvents || [];
  const topProducts = analytics?.topProducts || [];
  const topProductCategories = analytics?.topProductCategories || [];
  const totalEvents = Math.max(1, safeNumber(totals.events));
  const totalProductViews = Math.max(1, safeNumber(totals.productViews));
  const rangeDays = Math.max(1, safeNumber(analytics?.range?.days));
  const productViewsPerDay = safeNumber(totals.productViews) / rangeDays;
  const busiestHour = insights.busiestHour
    ? `${String(insights.busiestHour.hour).padStart(2, "0")}:00`
    : "Sin datos";

  return (
    <Stack spacing={1.2}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: WEB_STATS_BRAND.orange, flexShrink: 0 }}><QueryStatsRoundedIcon /></Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: WEB_STATS_BRAND.dark, fontSize: 22, lineHeight: 1.1 }}>Actividad del negocio</Typography>
            <Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>Operaciones, equipo, web y recorridos.</Typography>
          </Box>
        </Stack>
        <Button onClick={onRefresh} disabled={loading || Boolean(rangeError)} size="small" variant="outlined" sx={{ minWidth: 38, width: 38, height: 38, borderRadius: 999, p: 0 }}><RefreshRoundedIcon fontSize="small" /></Button>
      </Stack>

      <Paper elevation={0} sx={{ p: 1.1, borderRadius: 3, border: `1px solid ${WEB_STATS_BRAND.border}` }}>
        <Box sx={{ display: "flex", gap: .6, overflowX: "auto", pb: .25 }}>
          {PRESETS.map((preset) => (
            <Button
              key={preset.key}
              size="small"
              variant={range.preset === preset.key ? "contained" : "outlined"}
              onClick={() => onPreset(preset.key)}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, minWidth: "auto", px: 1.25, flexShrink: 0, bgcolor: range.preset === preset.key ? WEB_STATS_BRAND.orange : undefined }}
            >
              {preset.label}
            </Button>
          ))}
        </Box>
      </Paper>

      {range.preset === "custom" ? (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Desde"
              value={range.dateFrom}
              onChange={(event) => onRangeChange("dateFrom", event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: range.dateTo || undefined }}
              error={Boolean(rangeError)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Hasta"
              value={range.dateTo}
              onChange={(event) => onRangeChange("dateTo", event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: range.dateFrom || undefined }}
              error={Boolean(rangeError)}
            />
          </Grid>
          {rangeError ? <Grid item xs={12}><Alert severity="warning" sx={{ py: .4, borderRadius: 2.5 }}>{rangeError}</Alert></Grid> : null}
        </Grid>
      ) : null}

      <MobileStatsNav />

      {error ? <Alert severity="error" sx={{ borderRadius: 2.5, py: .5 }}>{error}</Alert> : null}

      <Grid id="stats-summary" container spacing={1} sx={{ scrollMarginTop: 120, "@media (max-width:339px)": { "& > .MuiGrid-item": { flexBasis: "100%", maxWidth: "100%" } } }}>
        <Grid item xs={6}><WebsiteMetricCard compact icon={<HistoryRoundedIcon />} label="Movimientos" value={formatNumber(totals.events)} helper={`${formatNumber(totals.averagePerActiveDay, 1)} por día activo`} comparison={comparisons.events} /></Grid>
        <Grid item xs={6}><WebsiteMetricCard compact icon={<BoltRoundedIcon />} label="Operaciones" value={formatNumber(totals.operations)} helper={`${formatPercent(insights.operationShare)} del total`} comparison={comparisons.operations} /></Grid>
        <Grid item xs={6}><WebsiteMetricCard compact icon={<PeopleAltRoundedIcon />} label="Usuarios activos" value={formatNumber(totals.activeUsers)} helper={`${formatNumber(totals.uniqueVisitors)} visitantes web`} comparison={comparisons.activeUsers} /></Grid>
        <Grid item xs={6}><WebsiteMetricCard compact icon={<RouteRoundedIcon />} label="Sesiones" value={formatNumber(totals.sessions)} helper={`${formatNumber(totals.averageSessionSteps, 1)} pasos promedio`} comparison={comparisons.sessions} /></Grid>
      </Grid>

      <MobileSection icon={<QueryStatsRoundedIcon />} title="Movimiento diario" helper="Actividad total y operaciones concretas.">
        <ActivityChart rows={analytics?.daily || []} compact />
      </MobileSection>

      <MobileSection icon={<AccessTimeRoundedIcon />} title="Lectura rápida" helper="El ritmo general del período seleccionado.">
        <Grid container spacing={.8}>
          <Grid item xs={6}><MiniInsight label="DÍAS ACTIVOS" value={`${formatNumber(totals.activeDays)} / ${formatNumber(analytics?.range?.days)}`} helper="Días con movimiento" /></Grid>
          <Grid item xs={6}><MiniInsight label="HORA MÁS ACTIVA" value={busiestHour} helper={insights.busiestHour ? `${formatNumber(insights.busiestHour.count)} movimientos` : "Sin datos"} /></Grid>
          <Grid item xs={6}><MiniInsight label="MEJOR DÍA" value={insights.busiestDay ? formatDate(insights.busiestDay.date) : "Sin datos"} helper={insights.busiestDay ? `${formatNumber(insights.busiestDay.count)} movimientos` : "Sin datos"} /></Grid>
          <Grid item xs={6}><MiniInsight label="ALERTAS" value={formatNumber(safeNumber(totals.warnings) + safeNumber(totals.critical))} helper={`${formatNumber(totals.success)} exitosos`} /></Grid>
        </Grid>
      </MobileSection>

      <MobileSection id="stats-products" icon={<VisibilityRoundedIcon />} title="Interés en productos" helper="Cómo crecieron las visitas a fichas durante el período.">
        <ProductVisitsChart rows={analytics?.daily || []} compact />
        <Grid container spacing={.8} sx={{ mt: .5 }}>
          <Grid item xs={6}><MiniInsight label="VISTAS A FICHAS" value={formatNumber(totals.productViews)} helper={`${formatNumber(topProducts.length)} ítems en ranking`} /></Grid>
          <Grid item xs={6}><MiniInsight label="PROMEDIO DIARIO" value={formatNumber(productViewsPerDay, 1)} helper={`Sobre ${formatNumber(rangeDays)} días`} /></Grid>
        </Grid>
        <Box sx={{ mt: 1 }}><TrendChip comparison={comparisons.productViews} compact /></Box>
      </MobileSection>

      <MobileSection collapsible icon={<Inventory2RoundedIcon />} title="Más visitados" helper="Ranking de productos y servicios.">
        <Stack spacing={.75}>
          {topProducts.slice(0, 8).map((row, index) => <MobileTopProduct key={row.productId || `${row.name}-${index}`} row={row} index={index} />)}
          {!topProducts.length ? <Alert severity="info" sx={{ py: .4 }}>Todavía no hay visitas a productos o servicios.</Alert> : null}
        </Stack>
      </MobileSection>

      <MobileSection collapsible icon={<CategoryRoundedIcon />} title="Categorías más visitadas" helper="Abrí para ver el ranking por grupo.">
        <Stack spacing={1.15}>
          {topProductCategories.slice(0, 8).map((row) => (
            <BreakdownRow
              key={row.category}
              compact
              label={catalogCategoryLabel(row.category)}
              value={row.views}
              total={totalProductViews}
              helper={`${formatNumber(row.uniqueProducts)} ${safeNumber(row.uniqueProducts) === 1 ? "ítem" : "ítems"} · ${formatNumber(row.uniqueVisitors)} visitantes`}
              color={WEB_STATS_BRAND.blue}
            />
          ))}
          {!topProductCategories.length ? <Alert severity="info" sx={{ py: .4 }}>Las categorías aparecerán cuando reciban visitas.</Alert> : null}
        </Stack>
      </MobileSection>

      <MobileSection collapsible icon={<CategoryRoundedIcon />} title="Áreas con más movimiento" helper="Distribución del historial del negocio.">
        <Stack spacing={1.25}>
          {categories.slice(0, 7).map((row) => <BreakdownRow key={row.key} compact label={categoryLabel(row.key)} value={row.count} total={totalEvents} helper={`${formatPercent(row.percent)} del total`} />)}
          {!categories.length ? <Alert severity="info" sx={{ py: .4 }}>No hay áreas con actividad.</Alert> : null}
        </Stack>
      </MobileSection>

      <MobileSection collapsible icon={<DashboardRoundedIcon />} title="Origen de la actividad" helper="Panel, web y procesos automáticos.">
        <Stack spacing={1.15}>
          {sources.map((row, index) => (
            <BreakdownRow key={row.key} compact label={sourceLabel(row.key)} value={row.count} total={totalEvents} helper={`${formatPercent(row.percent)} del total`} color={[WEB_STATS_BRAND.orange, WEB_STATS_BRAND.blue, WEB_STATS_BRAND.purple, WEB_STATS_BRAND.green][index % 4]} />
          ))}
          {!sources.length ? <Alert severity="info" sx={{ py: .4 }}>No hay orígenes para mostrar.</Alert> : null}
        </Stack>
      </MobileSection>

      <MobileSection collapsible id="stats-actions" icon={<TouchAppRoundedIcon />} title="Acciones más repetidas" helper="Abrí para ver ranking y volumen.">
        {actions.length ? (
          <Box sx={{ p: 1.05, mb: 1.1, borderRadius: 2.5, bgcolor: WEB_STATS_BRAND.background }}>
            <ActionBarsChart rows={actions} compact limit={6} />
          </Box>
        ) : null}
        <Stack spacing={.75}>
          {actions.slice(0, 9).map((row, index) => (
            <Stack key={`${row.action}-${row.category}-${row.source}`} direction="row" spacing={.8} alignItems="center" sx={{ p: .9, borderRadius: 2.3, bgcolor: WEB_STATS_BRAND.background }}>
              <Box sx={{ width: 24, height: 24, borderRadius: 1.6, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: WEB_STATS_BRAND.orange, fontWeight: 950, fontSize: 12 }}>{index + 1}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap variant="body2" sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark }}>{row.label}</Typography>
                <Typography noWrap variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{categoryLabel(row.category)} · {sourceLabel(row.source)}</Typography>
              </Box>
              <Chip size="small" label={formatNumber(row.count)} sx={{ fontWeight: 950 }} />
            </Stack>
          ))}
          {!actions.length ? <Alert severity="info" sx={{ py: .4 }}>No hay acciones registradas.</Alert> : null}
        </Stack>
      </MobileSection>

      <MobileSection collapsible id="stats-details" icon={<PeopleAltRoundedIcon />} title="Equipo activo" helper="Quiénes usaron el panel en este período.">
        <Stack spacing={.75}>
          {team.slice(0, 8).map((person, index) => (
            <Stack key={person.userId || `${person.email}-${index}`} direction="row" spacing={.8} alignItems="center" sx={{ p: .9, borderRadius: 2.3, bgcolor: WEB_STATS_BRAND.background }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1.8, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: WEB_STATS_BRAND.orange, fontWeight: 950 }}>{index + 1}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap variant="body2" sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark }}>{person.name || person.email || "Usuario"}</Typography>
                <Typography noWrap variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{person.role || "Miembro"} · {formatDateTime(person.lastAt)}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}><Typography sx={{ fontWeight: 950, color: WEB_STATS_BRAND.dark }}>{formatNumber(person.events)}</Typography><Typography variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>eventos</Typography></Box>
            </Stack>
          ))}
          {!team.length ? <Alert severity="info" sx={{ py: .4 }}>No hubo usuarios activos.</Alert> : null}
        </Stack>
      </MobileSection>

      <MobileSection collapsible icon={<RouteRoundedIcon />} title="Secciones con más movimiento" helper="Rutas del panel y de la web pública.">
        <Stack spacing={.7}>
          {paths.slice(0, 8).map((row) => (
            <Stack key={`${row.channel}-${row.path}`} direction="row" spacing={.8} alignItems="center" sx={{ py: .7 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap variant="body2" sx={{ fontWeight: 900, color: WEB_STATS_BRAND.dark }}>{row.path || "/"}</Typography>
                <Typography noWrap variant="caption" sx={{ color: WEB_STATS_BRAND.muted }}>{sourceLabel(row.channel)}</Typography>
              </Box>
              <Chip size="small" label={formatNumber(row.count)} />
            </Stack>
          ))}
          {!paths.length ? <Alert severity="info" sx={{ py: .4 }}>No hay recorridos suficientes.</Alert> : null}
        </Stack>
      </MobileSection>

      <MobileSection collapsible icon={<HistoryRoundedIcon />} title="Últimos movimientos" helper="Eventos recientes; cada uno abre su detalle.">
        <Box>
          {recentEvents.slice(0, 12).map((event) => <MobileEvent key={event.id} event={event} />)}
          {!recentEvents.length ? <Alert severity="info" sx={{ py: .4 }}>No hubo movimientos en este período.</Alert> : null}
        </Box>
      </MobileSection>
    </Stack>
  );
}
